import { baseApi as api } from "../baseApi";
const injectedRtkApi = api.injectEndpoints({
  endpoints: (build) => ({
    listCurrentTransportOrders: build.query<
      ListCurrentTransportOrdersApiResponse,
      ListCurrentTransportOrdersApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/current`,
        params: {
          search: queryArg.search,
          offset: queryArg.offset,
          limit: queryArg.limit,
          sortBy: queryArg.sortBy,
          sortDirection: queryArg.sortDirection,
        },
      }),
    }),
    listTransportOrders: build.query<
      ListTransportOrdersApiResponse,
      ListTransportOrdersApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders`,
        params: {
          state: queryArg.state,
          search: queryArg.search,
          offset: queryArg.offset,
          limit: queryArg.limit,
          sortBy: queryArg.sortBy,
          sortDirection: queryArg.sortDirection,
        },
      }),
    }),
    createTransportOrder: build.mutation<
      CreateTransportOrderApiResponse,
      CreateTransportOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders`,
        method: "POST",
        body: queryArg.createTransportOrder,
      }),
    }),
    listEligibleTransportOrderVehicles: build.query<
      ListEligibleTransportOrderVehiclesApiResponse,
      ListEligibleTransportOrderVehiclesApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/eligible-vehicles`,
        params: {
          pickupFrom: queryArg.pickupFrom,
          pickupTo: queryArg.pickupTo,
          deliveryFrom: queryArg.deliveryFrom,
          deliveryTo: queryArg.deliveryTo,
          excludeTransportOrderId: queryArg.excludeTransportOrderId,
        },
      }),
    }),
    getTransportOrder: build.query<
      GetTransportOrderApiResponse,
      GetTransportOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/${queryArg.transportOrderId}`,
      }),
    }),
    updateTransportOrder: build.mutation<
      UpdateTransportOrderApiResponse,
      UpdateTransportOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/${queryArg.transportOrderId}`,
        method: "PATCH",
        body: queryArg.updateTransportOrder,
      }),
    }),
    cancelTransportOrder: build.mutation<
      CancelTransportOrderApiResponse,
      CancelTransportOrderApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/${queryArg.transportOrderId}/cancel`,
        method: "POST",
      }),
    }),
    getTransportOrderRoute: build.query<
      GetTransportOrderRouteApiResponse,
      GetTransportOrderRouteApiArg
    >({
      query: (queryArg) => ({
        url: `/api/v1/transport-orders/${queryArg.transportOrderId}/route`,
      }),
    }),
  }),
  overrideExisting: false,
});
export { injectedRtkApi as transportOrdersApi };
export type ListCurrentTransportOrdersApiResponse =
  /** status 200 Paginated list of current transport orders. */ TransportOrderPage;
export type ListCurrentTransportOrdersApiArg = {
  /** Full text search field. */
  search?: string;
  /** For pagination; zero-based number of matching transport orders to skip. */
  offset?: number;
  /** For pagination; maximum number of matching transport orders returned. */
  limit?: number;
  /** Primary sort field. When omitted the server applies a severity-based sort (STOPPED → DELAYED → ON_TIME → COMPLETED). */
  sortBy?: SortFields;
  /** Sort direction used with `sortBy`. Defaults to ascending. */
  sortDirection?: SortDirection;
};
export type ListTransportOrdersApiResponse =
  /** status 200 Paginated list of transport orders. */ TransportOrderPage;
export type ListTransportOrdersApiArg = {
  /** Filter by transport order state. */
  state?: TransportOrderState;
  /** Full text search field. */
  search?: string;
  /** For pagination; zero-based number of matching transport orders to skip. */
  offset?: number;
  /** For pagination; maximum number of matching transport orders returned. */
  limit?: number;
  /** Primary sort field. Defaults to `pickupFrom`. */
  sortBy?: SortFields;
  /** Sort direction used with `sortBy`. Defaults to ascending. */
  sortDirection?: SortDirection;
};
export type CreateTransportOrderApiResponse =
  /** status 201 Transport order created. */ TransportOrderDetail;
export type CreateTransportOrderApiArg = {
  createTransportOrder: CreateTransportOrder;
};
export type ListEligibleTransportOrderVehiclesApiResponse =
  /** status 200 Vehicles eligible for the requested time window. */ EligibleVehicle[];
export type ListEligibleTransportOrderVehiclesApiArg = {
  /** Start of the requested pickup window. */
  pickupFrom: string;
  /** End of the requested pickup window. */
  pickupTo: string;
  /** Start of the requested delivery window. */
  deliveryFrom: string;
  /** End of the requested delivery window. */
  deliveryTo: string;
  /** Planned transport order to exclude from reservation conflict checks. Use when
    changing its own time window or assigned vehicle.
     */
  excludeTransportOrderId?: string;
};
export type GetTransportOrderApiResponse =
  /** status 200 Transport-order detail. */ TransportOrderDetail;
export type GetTransportOrderApiArg = {
  /** Unique transport-order identifier. */
  transportOrderId: string;
};
export type UpdateTransportOrderApiResponse =
  /** status 200 Updated transport order. */ TransportOrderDetail;
export type UpdateTransportOrderApiArg = {
  /** Unique transport-order identifier. */
  transportOrderId: string;
  updateTransportOrder: UpdateTransportOrder;
};
export type CancelTransportOrderApiResponse =
  /** status 200 Transport order cancelled. */ TransportOrderDetail;
export type CancelTransportOrderApiArg = {
  /** Unique transport-order identifier. */
  transportOrderId: string;
};
export type GetTransportOrderRouteApiResponse =
  /** status 200 Transport order route with waypoints. */ TransportOrderRoute;
export type GetTransportOrderRouteApiArg = {
  /** Unique transport-order identifier. */
  transportOrderId: string;
};
export type TransportOrderState =
  | "PLANNED"
  | "IN_PREPARATION"
  | "IN_TRANSIT"
  | "ARRIVED"
  | "COMPLETED"
  | "CANCELLED";
export type HubRef = {
  /** Hub identifier. */
  id: string;
  /** Hub display name. */
  name: string;
};
export type TimeWindow = {
  from: string;
  to: string;
};
export type Vin = string;
export type DeliveryStatus = "ON_TIME" | "DELAYED" | "STOPPED";
export type GeoPoint = {
  latitude: number;
  longitude: number;
};
export type ProgressDetails = {
  /** Estimated time of arrival at the delivery hub. */
  eta: string;
  /** Distance remaining to the delivery hub in meters. */
  remainingDistanceMeters: number;
  /** Distance already traveled in meters. */
  traveledDistanceMeters: number;
  /** Total route distance in meters. */
  totalDistanceMeters: number;
  currentPosition: GeoPoint;
  /** Timestamp when this progress snapshot was last updated. */
  updatedAt: string;
};
export type TransportOrderListItem = {
  transportOrderId: string;
  state: TransportOrderState;
  launchHub: HubRef;
  landingHub: HubRef;
  pickupWindow: TimeWindow;
  deliveryWindow: TimeWindow;
  loadId: string;
  vin: Vin;
  createdAt: string;
  updatedAt: string;
  deliveryStatus?: DeliveryStatus;
  /** Timestamp when the vehicle was dispatched. */
  dispatchedAt?: string;
  progressDetails?: ProgressDetails;
  /** Timestamp when the vehicle arrived at the landing hub. */
  deliveredAt?: string;
  /** Actual delivery duration in seconds after vehicle arrived at the landing hub. */
  actualDurationSeconds?: number;
};
export type PaginationMetadata = {
  /** Zero-based offset of the first returned matching transport order. */
  offset: number;
  /** Maximum number of transport orders requested. */
  limit: number;
  /** Total number of transport orders matching the supplied filters. */
  total: number;
};
export type TransportOrderPage = {
  items: TransportOrderListItem[];
  pagination: PaginationMetadata;
};
export type ErrorResponse = {
  /** Human-readable error message. */
  message: string;
  /** HTTP status code. */
  code: number;
};
export type SortFields =
  | "pickupFrom"
  | "deliveryFrom"
  | "eta"
  | "dispatchedAt"
  | "deliveredAt"
  | "deliveryStatus"
  | "state";
export type SortDirection = "asc" | "desc";
export type RouteInfo = {
  /** Total route distance in meters. */
  totalDistanceMeters: number;
  /** Estimated total route duration in seconds. */
  totalEstimatedDurationSeconds: number;
};
export type TransportOrderMilestoneType =
  "DISPATCHED" | "STOPPED" | "DELIVERED";
export type TransportOrderMilestone = {
  type: TransportOrderMilestoneType;
  occurredAt: string;
  position?: GeoPoint;
};
export type TransportOrderDetail = TransportOrderListItem & {
  routeInfo?: RouteInfo;
  /** Ordered lifecycle history. Empty for planned orders.
    A transport order can contain multiple stopped milestones.
     */
  milestones: TransportOrderMilestone[];
};
export type CreateTransportOrder = {
  launchHubId: string;
  landingHubId: string;
  pickupWindow: TimeWindow;
  deliveryWindow: TimeWindow;
  loadId: string;
  vin: Vin;
};
export type EligibleVehicle = {
  vin: Vin;
  /** Vehicle display name. */
  name: string;
  /** Vehicle license plate number. */
  licensePlate?: string;
};
export type UpdateTransportOrder = {
  launchHubId?: string;
  landingHubId?: string;
  pickupWindow?: TimeWindow;
  deliveryWindow?: TimeWindow;
  loadId?: string;
  vin?: Vin;
};
export type TransportOrderRoute = {
  /** Total route distance in meters. */
  totalDistanceMeters: number;
  /** Estimated total route duration in seconds. */
  totalEstimatedDurationSeconds: number;
  /** Ordered list of coordinates along the route. */
  waypoints: GeoPoint[];
};
export const {
  useListCurrentTransportOrdersQuery,
  useListTransportOrdersQuery,
  useCreateTransportOrderMutation,
  useListEligibleTransportOrderVehiclesQuery,
  useGetTransportOrderQuery,
  useUpdateTransportOrderMutation,
  useCancelTransportOrderMutation,
  useGetTransportOrderRouteQuery,
} = injectedRtkApi;
